import { getNode } from '@udecode/plate-core';
import React, {
  createContext, SetStateAction, Dispatch, ReactNode, useState, useContext, useEffect, ReactElement,
} from 'react';
import { Path } from 'slate';
import { toast } from 'react-toastify';
import { CollectionElement } from '@typedream/core';
import {
  App,
  isAllowedCreatePage,
  buildPageCustomizationCollectionDataUpdateRequests,
  SPACE_ACTION,
} from '@typedream/data';
import { v4 as uuidv4 } from 'uuid';
import {
  IApp,
  ICollectionColumn,
  ICollectionData,
  ICollectionDataItem,
  ICollectionIntegration,
  ICollectionTable,
  IConnectCollectionRequest,
  IItemPageSchema,
} from '../../../../types';
import { errToString } from '../../../../utils/errors';
import TokenContext from '../../../../utils/jwt/tokenContext';
import { CollectionSettingsOpenContext } from '../../context/CollectionSettingsContextProvider/openContext';
import { CollectionSettingsPathContext } from '../../context/CollectionSettingsContextProvider/pathContext';
import { BLOCK_ELEMENT_KEY } from '../../types';
import { SETTINGS_PAGE } from './constants';
import colErrToString from '../../utils/collectionErrToString';
import { EDITOR_DOMAIN, SPACE_SUBSCRIPTIONS } from '../../../../constants';
import { useSpaceAndPageIDFromRouter } from '../../../../data/space/hooks';
import {
  useGetPageQuery,
  useStore,
  useUpdatePageCustomizationMutation,
  useGetSpaceQuery,
} from '../../../../store';
import { useTEditorRef } from '../../plate';
import { api } from '../../../../data/api';
/**
     * The Collection Settings Context
     */

export interface CollectionSettingsDataInterface {
  connectedIntegrations: IApp[],
  loading: boolean,
  tableConnected: boolean,
  err: string | ReactNode,
  seterr: Dispatch<SetStateAction<Element | string | null | ReactNode>>,
  listConnectedIntegrations: () => Promise<void>,
  collectionData: ICollectionDataItem,
  grabElement: (_path: Path) => void,
  element: CollectionElement,
  updatePageCollectionData: (collData: ICollectionData) => Promise<void>,
  listColumns: (_collectionData: ICollectionDataItem) => Promise<{
    [key: string]: ICollectionColumn;
  }>,
  columns: { [key: string]: ICollectionColumn },
  editorSyncData: (_collectionData: ICollectionDataItem) => Promise<void>,
  tables: ICollectionTable[],
  listTables: () => Promise<void>,
  addColumnNames: (
    columnsToAdd: ICollectionColumn[],
    _collectionData: ICollectionDataItem,
  ) => Promise<void>,
  connectCollection: (connectCollectionData: IConnectCollectionRequest) => Promise<void>,
  connectCollectionGenerateColumns: (
    _collectionData: ICollectionDataItem,
    _itemPageSchema: IItemPageSchema,
  ) => Promise<void>,
  getDefaultItemPageSchema: () => IItemPageSchema,
  upgradeRequired: boolean,
  loadingListTables: boolean,
  setloadingListTables: Dispatch<SetStateAction<boolean>>,
  loadingListColumns: boolean,
  useIntegrationOAuth: (connect: boolean, code: string) => Promise<void>,
  totalItemsGeneratedColumns: number,
}

export const CollectionSettingsPageContext = createContext<SETTINGS_PAGE>(
  SETTINGS_PAGE.CONNECT_DATA,
);
export const CollectionSettingsPageDispatchContext = createContext<Dispatch<
  SetStateAction<SETTINGS_PAGE>>>(undefined);

export const CollectionSettingsIntegrationContext = createContext<ICollectionIntegration>(
  ICollectionIntegration.NOTION,
);
export const CollectionSettingsIntegrationDispatchContext = createContext<Dispatch<
  SetStateAction<ICollectionIntegration>>>(undefined);

export const CollectionSettingsDataContext = createContext<CollectionSettingsDataInterface>(
  null,
);

export function CollectionSettingsPopupProvider({ children }: { children: ReactNode }) {
  const editor = useTEditorRef();
  const settingsOpen = useContext(CollectionSettingsOpenContext);
  const [settingsPage, setsettingsPage] = useState(null);
  const [integration, setintegration] = useState(ICollectionIntegration.NOTION);
  const [element, setelement] = useState<CollectionElement>(null);
  const [tables, settables] = useState([]);
  const [loadingListTables, setloadingListTables] = useState(false);
  const [loading, setloading] = useState(false);
  const [loadingListColumns, setloadingListColumns] = useState(false);
  const [connectedIntegrations, setconnectedIntegrations] = useState<IApp[]>([]);
  const [spaceID, pageID] = useSpaceAndPageIDFromRouter();
  const { data: page } = useGetPageQuery([spaceID, pageID]);
  const { data: space } = useGetSpaceQuery([spaceID]);
  const { spacePermissionTokenHandler, subscriptionTokenHandler } = useContext(
    TokenContext,
  );
  const [err, seterr] = useState<ReactElement | string>(null);
  const [columns, setcolumns] = useState<{ [key: string]: ICollectionColumn }>({});
  const [tableConnected, settableConnected] = useState(false);

  const [upgradeRequired, setupgradeRequired] = useState(true);

  const [totalItemsGeneratedColumns, settotalItemsGeneratedColumns] = useState(undefined);

  const resetValues = () => {
    setsettingsPage(null);
    setintegration(ICollectionIntegration.NOTION);
    setelement(null);
    settables([]);
    setloadingListTables(false);
    setloading(false);
    setloadingListColumns(false);
    setconnectedIntegrations([]);
    seterr(null);
    setcolumns({});
    settableConnected(false);
    setupgradeRequired(true);
  };

  useEffect(() => {
    if (!settingsOpen) {
      resetValues();
    }
  }, [settingsOpen]);

  const checkSubscription = async () => {
    const sub = await subscriptionTokenHandler.getSubscription(
        spaceID as string,
    );
    setupgradeRequired(!sub.isAllowed(SPACE_ACTION.Collection, SPACE_SUBSCRIPTIONS));
  };
  useEffect(() => {
    if (spaceID && settingsOpen) {
      checkSubscription();
    }
  }, [spaceID, settingsOpen]);

  const path = useContext(CollectionSettingsPathContext);

  const listConnectedIntegrations = async () => {
    setloading(true);
    try {
      const resp = await api().app().list(
        spaceID,
        spacePermissionTokenHandler,
        subscriptionTokenHandler,
      );
      setconnectedIntegrations(resp?.data);
    } catch (e) {
      seterr(colErrToString(space?.slug, spaceID, errToString(e)));
    }
    setloading(false);
  };

  useEffect(() => {
    if (spaceID && spacePermissionTokenHandler && subscriptionTokenHandler) {
      listConnectedIntegrations();
    }
  }, [spaceID, spacePermissionTokenHandler, subscriptionTokenHandler, settingsOpen]);

  const grabElement = (_path: Path) => {
    const el = getNode<CollectionElement>(editor, _path);
    setelement(el);
    return el;
  };

  useEffect(() => {
    if (path) {
      grabElement(path);
    }
  }, [path, settingsOpen, settingsPage]);

  const connectionDataID = element?.[BLOCK_ELEMENT_KEY.COLLECTION_DATA_ID];
  const collectionData = page?.customization?.collection_data?.[
    connectionDataID] as ICollectionDataItem;

  const checkTableConnected = () => {
    const connected = new App(api().api).app(collectionData)?.tableConnected(collectionData);
    settableConnected(connected);
  };
  useEffect(() => {
    if (collectionData && integration) {
      checkTableConnected();
    } else {
      settableConnected(false);
    }
  }, [collectionData, integration, settingsOpen]);

  useEffect(() => {
    setsettingsPage(null);
  }, [settingsOpen]);

  useEffect(() => {
    if (!settingsPage) {
      if (settingsOpen
        && connectionDataID
        && connectedIntegrations
        && collectionData?.integration
        && connectedIntegrations?.filter((i) => i.name === collectionData?.integration)?.length > 0
        && new App(api().api).app(collectionData)?.tableConnected(collectionData)) {
        setintegration(collectionData?.integration);
        setsettingsPage(SETTINGS_PAGE.CONNECT_TABLE);
      } else {
        setintegration(ICollectionIntegration.NOTION);
        setsettingsPage(SETTINGS_PAGE.CONNECT_DATA);
      }
    }
  }, [
    settingsPage,
    settingsOpen,
    collectionData?.integration,
    connectionDataID,
    connectedIntegrations,
    element,
    path]);

  const updatePageCollectionData = async (collData: ICollectionData) => {
    const updateRequests = buildPageCustomizationCollectionDataUpdateRequests(spaceID, collData);
    if (updateRequests?.length > 0) {
      try {
        await useUpdatePageCustomizationMutation.run([{
          spaceID,
          pageID,
          customizationUpdateRequests: updateRequests,
        }]).unwrap();
      } catch (error) {
        toast.error(errToString(error));
      }
    }
  };

  const listColumns = async (_collectionData: ICollectionDataItem) => {
    setloadingListColumns(true);
    try {
      const cols = await new App(api().api).app(_collectionData)?.getColumnNames(
        spaceID,
        _collectionData,
        spacePermissionTokenHandler,
        subscriptionTokenHandler,
      );
      setcolumns(cols);
      setloadingListColumns(false);
      return cols;
    } catch (e) {
      seterr(colErrToString(space?.slug, spaceID, errToString(e)));
    }
    setloadingListColumns(false);
  };

  const editorSyncData = async (_collectionData: ICollectionDataItem) => {
    const notionDataResults = await new App(api().api).app(_collectionData)?.getData(
      spaceID,
      _collectionData,
      undefined,
      spacePermissionTokenHandler,
      subscriptionTokenHandler,
    );
    const data: ICollectionData = {
      [element?.collection_data_id]: {
        ..._collectionData,
        integration,
        data: notionDataResults?.data,
      },
    };
    editor.synchronizeCollection(data, element.id);
  };
  const listTables = async () => {
    setloadingListTables(true);
    try {
      const tablesList = await new App(api().api).app(collectionData)?.listTables(
        spaceID,
        spacePermissionTokenHandler,
        subscriptionTokenHandler,
      );
      settables(tablesList);
    } catch (e) {
      seterr(colErrToString(space?.slug, spaceID, errToString(e)));
    }
    setloadingListTables(false);
  };

  const connectCollection = async (connectCollectionData: IConnectCollectionRequest) => {
    const localConnectCollectionData = connectCollectionData;
    // check if templatePage can be created
    const errTemplatePage = isAllowedCreatePage(
      localConnectCollectionData.template_page.slug,
      localConnectCollectionData.template_page.path,
      space?.routes,
      true,
    );
    if (errTemplatePage) {
      seterr(errTemplatePage);
      return;
    }
    // check if templateHomePage can be created
    const errTemplateHomePage = isAllowedCreatePage(
      localConnectCollectionData.template_home_page.slug,
      localConnectCollectionData.template_home_page.path,
      space?.routes,
      false,
    );
    if (errTemplateHomePage) {
      localConnectCollectionData.create_template_home_page = false;
      localConnectCollectionData.template_home_page = undefined;
      localConnectCollectionData.template_home_page_content = undefined;
      localConnectCollectionData.template_home_page_customization = undefined;
    }
    try {
      await api().app().connectCollection(
        spaceID,
        localConnectCollectionData,
        spacePermissionTokenHandler,
        subscriptionTokenHandler,
      );
      useStore.getState().spacesApi.invalidateQueries(['getSpace', spaceID]);
      useStore.getState().pagesApi.invalidateQueries(
        ['listPages', spaceID],
        ['getPage', spaceID, pageID],
      );
      checkTableConnected();
    } catch (e) {
      seterr(colErrToString(space?.slug, spaceID, errToString(e)));
    }
  };

  const connectCollectionGenerateColumns = async (
    _collectionData: ICollectionDataItem,
    _itemPageSchema: IItemPageSchema,
  ) => {
    const rawCollectionData:ICollectionDataItem = {
      ..._collectionData,
      metadata: undefined,
      data: undefined,
      hasMore: undefined,
      nextCursor: undefined,
      fetchedCursor: undefined,
      query: undefined,
      queryResp: undefined,
    };
    try {
      const addedColumns = await new App(api().api).app(
        _collectionData,
      )?.connectCollectionGenerateColumns(
        spaceID,
        _collectionData,
        spacePermissionTokenHandler,
        subscriptionTokenHandler,
      );

      await new App(api().api).app(_collectionData)?.prefillDefaultItemPage(
        spaceID,
        _itemPageSchema,
        addedColumns,
        rawCollectionData,
        spacePermissionTokenHandler,
        subscriptionTokenHandler,
        uuidv4,
        settotalItemsGeneratedColumns,
      );
      listColumns(_collectionData);
    } catch (e) {
      seterr(colErrToString(space?.slug, spaceID, errToString(e)));
    }
  };

  useEffect(() => {
    if (connectedIntegrations?.filter((i) => i.name === collectionData?.integration)?.length > 0) {
      listTables();
    }
  }, [spaceID, collectionData, connectedIntegrations, settingsOpen]);

  const addColumnNames = async (
    columnsToAdd: ICollectionColumn[],
    _collectionData: ICollectionDataItem,
  ) => {
    try {
      await new App(api().api).app(_collectionData)?.addColumnNames(
        spaceID,
        _collectionData,
        columnsToAdd,
        spacePermissionTokenHandler,
        subscriptionTokenHandler,
      );
      listColumns(_collectionData);
    } catch (e) {
      seterr(errToString(e));
    }
  };

  const useIntegrationOAuth = async (connect: boolean, code: string) => {
    seterr(null);
    const origin = typeof window !== 'undefined' ? window?.location?.origin : `https://${EDITOR_DOMAIN}`;
    if (connect) {
      await api().app().connectOAuth(
        spaceID,
        integration,
        code,
        origin,
        spacePermissionTokenHandler,
        subscriptionTokenHandler,
      );
    }
    listConnectedIntegrations();
    setintegration(integration);
  };

  const getDefaultItemPageSchema = () => new App(api().api).app(
    collectionData,
  )?.getDefaultItemPageSchema(
    Object.values(columns),
  );

  return (
    <CollectionSettingsPageContext.Provider value={settingsPage}>
      <CollectionSettingsPageDispatchContext.Provider value={setsettingsPage}>
        <CollectionSettingsIntegrationContext.Provider value={integration}>
          <CollectionSettingsIntegrationDispatchContext.Provider value={setintegration}>
            <CollectionSettingsDataContext.Provider value={
              {
                connectedIntegrations,
                loading,
                err,
                seterr,
                listConnectedIntegrations,
                collectionData,
                grabElement,
                element,
                updatePageCollectionData,
                columns,
                listColumns,
                editorSyncData,
                tableConnected,
                tables,
                listTables,
                addColumnNames,
                getDefaultItemPageSchema,
                connectCollection,
                connectCollectionGenerateColumns,
                upgradeRequired,
                loadingListTables,
                setloadingListTables,
                loadingListColumns,
                useIntegrationOAuth,
                totalItemsGeneratedColumns,
              }
            }
            >
              {children}
            </CollectionSettingsDataContext.Provider>
          </CollectionSettingsIntegrationDispatchContext.Provider>
        </CollectionSettingsIntegrationContext.Provider>
      </CollectionSettingsPageDispatchContext.Provider>
    </CollectionSettingsPageContext.Provider>
  );
}
